import React, { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { buildRoomCategoryDetails } from "./roomCategoryDetailData";
import "./RoomCategoryDetails.css";

function DetailIcon({ kind }) {
  return (
    <svg className="roomDetails__icon" data-detail-icon={kind} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      {kind === "view" ? <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="16" cy="8" r="2" />
        <path d="m3 17 6-7 5 6 3-3 4 5" />
      </> : kind === "highlights" ? <>
        <path d="m10 3 2.3 6.7L19 12l-6.7 2.3L10 21l-2.3-6.7L1 12l6.7-2.3L10 3Z" />
        <path d="m19 2 1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3Z" />
      </> : <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <path d="m14 18 2.5 2.5L22 15" />
      </>}
    </svg>
  );
}

function DetailSection({ kind, label, items, compact = false }) {
  const listRef = useRef(null);
  const [capacity, setCapacity] = useState(null);
  const headingId = useId();

  useLayoutEffect(() => {
    const node = listRef.current;
    if (!compact || kind === "view" || !node) return undefined;
    const measure = () => {
      const styles = window.getComputedStyle(node);
      const rowHeight = Number.parseFloat(styles.getPropertyValue("--room-detail-row-height")) || 16;
      const gap = Number.parseFloat(styles.rowGap) || 4;
      const fit = Math.max(0, Math.floor((node.clientHeight + gap) / (rowHeight + gap)));
      setCapacity(previous => previous === fit ? previous : fit);
    };
    measure();
    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [compact, kind]);

  const visible = compact && kind !== "view" ? items.slice(0, capacity ?? 0) : items;
  return (
    <section className={`roomDetails__section roomDetails__section--${kind}`} data-detail-kind={kind} aria-labelledby={headingId}>
      <h3 className="roomDetails__heading" id={headingId}><DetailIcon kind={kind} /><span>{label}</span></h3>
      <ul className="roomDetails__list" ref={listRef}>
        {visible.map(item => <li className="roomDetails__item" key={item} title={compact ? item : undefined}>{item}</li>)}
      </ul>
    </section>
  );
}

export default function RoomCategoryDetails({ beCopy, amenities, lang, roomName, t }) {
  const details = useMemo(() => buildRoomCategoryDetails({ beCopy, amenities, lang }), [beCopy, amenities, lang]);
  const [open, setOpen] = useState(false);
  const dialogTitleId = useId();
  const total = (details.view ? 1 : 0) + details.highlights.length + details.amenities.length;
  const hasCommercialDetails = !!details.view || details.highlights.length > 0;
  const labels = { view: t("room_view_label"), highlights: t("room_highlights_label"), amenities: t("room_amenities_label") };
  const section = (kind, items, compact) => items.length ? <DetailSection key={kind} kind={kind} label={labels[kind]} items={items} compact={compact} /> : null;

  return <>
    <div className="roomCard__mediaFeatures">
      {total ? <>
        <div className={`roomDetails__columns ${!hasCommercialDetails || !details.amenities.length ? "roomDetails__columns--single" : ""}`}>
          {hasCommercialDetails ? <div className="roomDetails__column roomDetails__column--commercial">
            {section("view", details.view ? [details.view] : [], true)}
            {section("highlights", details.highlights, true)}
          </div> : null}
          {details.amenities.length ? <div className="roomDetails__column roomDetails__column--amenities">
            {section("amenities", details.amenities, true)}
          </div> : null}
        </div>
        <button type="button" className="roomCard__mediaFeaturesAction" aria-haspopup="dialog"
          aria-label={`${t("room_details_all")}: ${roomName}`} onClick={() => setOpen(true)}>
          <span>{t("room_details_all")} <span className="roomDetails__count">({total})</span></span>
          <span aria-hidden="true">↗</span>
        </button>
      </> : <p className="roomDetails__empty">{t("room_details_empty")}</p>}
    </div>
    <Modal show={open} onHide={() => setOpen(false)} animation={false} centered scrollable backdropClassName="roomDetailsModalBackdrop"
      className="roomDetailsModalShell" contentClassName="roomDetailsModal" aria-labelledby={dialogTitleId}>
      <Modal.Header>
        <div className="roomDetailsModal__heading">
          <span className="roomDetailsModal__eyebrow">{t("room_features_title")}</span>
          <h2 id={dialogTitleId}>{roomName}</h2>
        </div>
        <button type="button" className="roomDetailsModal__close" onClick={() => setOpen(false)} aria-label={t("close")}>
          <span aria-hidden="true">×</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="roomDetailsModal__sections">
          {section("view", details.view ? [details.view] : [], false)}
          {section("highlights", details.highlights, false)}
          {section("amenities", details.amenities, false)}
        </div>
      </Modal.Body>
    </Modal>
  </>;
}
